import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type BadgeTone =
  'neutral' | 'strong' | 'primary' | 'success' | 'error' | 'warning' | 'info' | 'critical' | 'low';
/** soft = tinted bg + *-text · solid = saturated fill · outline = soft + 1px border · dashed = outline-only. */
export type BadgeVariant = 'soft' | 'solid' | 'outline' | 'dashed';
/** xs 20px/11px · sm 22px/12px (table cells) · md 24px/12px (identity pills). */
export type BadgeSize = 'xs' | 'sm' | 'md';
export type BadgeShape = 'pill' | 'rounded' | 'square';

const SOFT: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-text-2',
  strong: 'bg-surface-3 text-text',
  primary: 'bg-primary-soft text-primary-text',
  success: 'bg-success-soft text-success-text',
  error: 'bg-error-soft text-error-text',
  warning: 'bg-warning-soft text-warning-text',
  info: 'bg-info-soft text-info-text',
  critical: 'bg-error-soft text-error-text',
  low: 'bg-surface-2 text-low',
};

const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-text',
  strong: 'bg-inverse text-inverse-text',
  primary: 'bg-primary-solid text-white',
  success: 'bg-success text-white',
  error: 'bg-error text-white',
  warning: 'bg-warning text-on-warning',
  info: 'bg-info text-white',
  critical: 'bg-critical text-white',
  low: 'bg-low text-white',
};

const OUTLINE: Record<BadgeTone, string> = {
  neutral: 'border border-border bg-surface-2 text-text-2',
  strong: 'border border-text bg-surface-3 text-text',
  primary: 'border border-primary bg-primary-soft text-primary-text',
  success: 'border border-success bg-success-soft text-success-text',
  error: 'border border-error bg-error-soft text-error-text',
  warning: 'border border-warning bg-warning-soft text-warning-text',
  info: 'border border-info bg-info-soft text-info-text',
  critical: 'border border-critical bg-error-soft text-error-text',
  low: 'border border-low bg-surface-2 text-low',
};

const DOT: Record<BadgeTone, string> = {
  neutral: 'bg-text-3',
  strong: 'bg-text',
  primary: 'bg-primary',
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
  info: 'bg-info',
  critical: 'bg-critical',
  low: 'bg-low',
};

const SIZE: Record<BadgeSize, string> = {
  xs: 'h-5 gap-1 px-1.5 text-11',
  sm: 'h-[22px] gap-1.5 px-2 text-12',
  md: 'h-6 gap-1.5 px-2.5 text-12',
};

const SHAPE: Record<BadgeShape, string> = {
  pill: 'rounded-full',
  rounded: 'rounded-[6px]',
  square: 'rounded-sm',
};

export function badgeClassName({
  tone = 'neutral',
  variant = 'soft',
  size = 'md',
  shape = 'pill',
  weight = 'medium',
}: {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  shape?: BadgeShape;
  weight?: 'medium' | 'semibold' | 'bold';
} = {}): string {
  return cn(
    'inline-flex shrink-0 items-center whitespace-nowrap leading-none',
    SIZE[size],
    SHAPE[shape],
    weight === 'bold' ? 'font-bold' : weight === 'semibold' ? 'font-semibold' : 'font-medium',
    variant === 'solid'
      ? SOLID[tone]
      : variant === 'outline'
        ? OUTLINE[tone]
        : variant === 'dashed'
          ? 'border border-dashed border-border bg-transparent text-text-2'
          : SOFT[tone],
  );
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  size?: BadgeSize;
  shape?: BadgeShape;
  weight?: 'medium' | 'semibold' | 'bold';
  /** Leading 6px status dot in the tone's saturated colour. */
  dot?: boolean;
  /** Leading icon (Lucide element; sized 12px unless `iconSize` is set). */
  icon?: ReactNode;
  iconSize?: 12 | 14;
}

/** Generic badge / pill / chip. Semantic identities (status, severity…) are built on it. */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  {
    tone = 'neutral',
    variant = 'soft',
    size = 'md',
    shape = 'pill',
    weight = 'medium',
    dot = false,
    icon,
    iconSize = 12,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(badgeClassName({ tone, variant, size, shape, weight }), className)}
      {...rest}
    >
      {dot ? (
        <span data-slot="icon" aria-hidden className={cn('size-1.5 shrink-0 rounded-full', DOT[tone])} />
      ) : null}
      {icon ? (
        <span
          data-slot="icon"
          aria-hidden
          className={cn('inline-flex shrink-0', iconSize === 14 ? '[&>svg]:size-3.5' : '[&>svg]:size-3')}
        >
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
});

/** Pill-shaped {@link Badge}. */
export const Pill = forwardRef<HTMLSpanElement, BadgeProps>(function Pill(props, ref) {
  return <Badge ref={ref} shape="pill" {...props} />;
});
